import { Link } from "wouter";
import { SEARCH_AI_NOTICE } from "@/lib/searchNotice";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useStructuredData } from "@/hooks/useStructuredData";
import { buildCorporateSafeToolsSchema } from "@/lib/hubSchema";
import { getToolById } from "@/lib/tools-data";
import { getToolName } from "@/lib/i18n/tool-name-translations";
import { useLocale } from "@/hooks/useLocale";
import { BackToHomeLink } from "@/components/BackToHomeLink";
import { AiPolicySelector } from "@/components/AiPolicySelector";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

// A deliberately broad, cross-category sample -- the point of this page is
// "the whole site works this way," not just one category.
const FEATURED_TOOL_IDS = [
  "merge-pdf",
  "compress-image",
  "word-counter",
  "invoice-generator",
  "qr-generator",
  "unit-converter",
  "resume-builder",
  "password-generator",
];

export default function CorporateSafeTools() {
  const { locale, localizedHref } = useLocale();
  usePageMeta({
    title: "Corporate-Safe Tools \u2014 Works Under Any AI Policy",
    description:
      "300+ professional tools, premium applications and workflow products for PDF, images, text, and documents that need no AI, no signup, and process files in your browser — works the same whether your company blocks AI, allows it through approved vendors, or has no restrictions at all.",
    path: "/corporate-safe-tools",
  });

  const featuredTools = FEATURED_TOOL_IDS.map(id => getToolById(id)).filter(
    (t): t is NonNullable<typeof t> => Boolean(t)
  );

  useStructuredData(buildCorporateSafeToolsSchema({ tools: featuredTools.map((t) => ({ name: t.name, path: `/tool/${t.id}` })) }));

  return (
    <div className="page-enter">
      <section className="border-b border-border/50 bg-muted/20">
        <div className="container max-w-3xl mx-auto py-14 px-4">
          <BackToHomeLink />
          <div className="flex items-center gap-2 mb-4 text-primary">
            <ShieldCheck className="h-6 w-6" />
            <span className="text-sm font-semibold uppercase tracking-wide">
              Works under any company AI policy
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">
            Your files stay on your device — no matter your
            company's AI policy
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            Whether AI tools are fully blocked, allowed through approved
            vendors only, or unrestricted on your work network,
            FreeDailyPro works exactly the same. Its 300+ professional tools, premium
            applications and workflow products for PDF, images, text, and
            documents run in your browser — no
            AI, and for these tools your files never leave your device. The few
            server-side features are labeled on their own pages.
          </p>
          <p className="text-sm text-muted-foreground leading-relaxed mt-3">
            <strong>One clarification:</strong> {SEARCH_AI_NOTICE} If AI
            services are blocked on your network, search simply falls back
            to standard results.
          </p>
        </div>
      </section>

      <section className="container max-w-3xl mx-auto py-12 px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-12">
          <div className="p-5 rounded-xl border bg-card">
            <Lock className="h-5 w-5 text-primary mb-2" />
            <p className="font-semibold text-sm mb-1">Your files are not sent to an AI</p>
            <p className="text-sm text-muted-foreground">
              Every core file tool processes your file directly in your browser,
              with no AI model involved — nothing about your files for an
              IT policy to trip over. (Search is the one place a language
              model can be used; see the note above.)
            </p>
          </div>
          <div className="p-5 rounded-xl border bg-card">
            <CheckCircle2 className="h-5 w-5 text-primary mb-2" />
            <p className="font-semibold text-sm mb-1">Not a workaround</p>
            <p className="text-sm text-muted-foreground">
              This isn't a way around your company's AI policy — it's a
              genuinely different kind of tool that was never subject to it in
              the first place.
            </p>
          </div>
          <div className="p-5 rounded-xl border bg-card">
            <ShieldCheck className="h-5 w-5 text-primary mb-2" />
            <p className="font-semibold text-sm mb-1">No account required</p>
            <p className="text-sm text-muted-foreground">
              No Professional Tool needs a sign-up to use, including file-based ones, so
              there's nothing to explain to IT before you start using them.
            </p>
          </div>
        </div>

        <h2 className="text-xl font-semibold mb-4">
          A few tools to start with
        </h2>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-12">
          {featuredTools.map(tool => (
            <Link
              key={tool.id}
              href={localizedHref(`/tool/${tool.id}`)}
              className="p-3 rounded-lg border bg-card hover:border-primary/40 transition-colors text-center"
            >
              <span className="text-sm font-medium">{getToolName(locale, tool.id, tool.name)}</span>
            </Link>
          ))}
        </div>

        <div className="p-6 rounded-xl border bg-muted/30">
          <h2 className="text-lg font-semibold mb-2">
            Planning ahead for optional AI features
          </h2>
          <p className="text-sm text-muted-foreground mb-4">
            We're building a small set of optional AI-powered tools as a
            separate, clearly-marked layer on top of the tools above —
            never a replacement for them, and never required to use anything on
            this site. If you'd like your preference remembered ahead of time,
            you can set it now.
          </p>
          <AiPolicySelector
            trigger={
              <Button variant="outline" size="sm">
                Set your AI policy preference
              </Button>
            }
          />
        </div>
      </section>
    </div>
  );
}
